import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Minus, Plus, Trash2, ShoppingBag, Loader2, MessageCircle, Check, Truck, ExternalLink, Info, Store, Users } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { CONTACTS } from "@/data/contacts";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialCustomer = { name: "", phone: "", type: "takeaway", tableNumber: "", notes: "" };

function fmtPrice(n) { return `€${n.toFixed(n % 1 ? 2 : 0)}`; }

export default function CartDrawer() {
  const { items, totals, add, dec, remove, clear, open, setOpen, tableFromQR } = useCart();
  const [customer, setCustomer] = useState(() => ({
    ...initialCustomer,
    type: tableFromQR ? "dinein" : "takeaway",
    tableNumber: tableFromQR || "",
  }));
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const { t } = useLang();

  const setField = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));

  const validate = () => {
    if (!customer.name.trim()) return "Inserisci il tuo nome.";
    if (!/^[+0-9\s\-()]{5,}$/.test(customer.phone)) return "Telefono non valido.";
    if (items.length === 0) return "Il carrello è vuoto.";
    return null;
  };

  const buildWhatsappText = (order) => {
    const dateFmt = format(new Date(), "d MMM · HH:mm", { locale: it });
    const modeLabel = order.order_type === "dinein"
      ? (order.table_number ? `Al tavolo n. ${order.table_number}` : "Al tavolo")
      : "Asporto (ritiro in ristorante)";
    const lines = [
      "*NUOVO ORDINE — Drago Ramen*",
      "————————————————",
      `*Cliente:* ${order.customer_name}`,
      `*Telefono:* ${order.customer_phone}`,
      `*Modalità:* ${modeLabel}`,
      `*Data richiesta:* ${dateFmt}`,
      `*Codice:* ${order.id.slice(0, 8).toUpperCase()}`,
      "————————————————",
      "*Piatti ordinati:*",
    ];
    order.items.forEach((it) => {
      const optStr = it.option ? ` (${it.option})` : "";
      lines.push(`• ${it.qty}× ${it.name}${optStr} — ${fmtPrice(it.price * it.qty)}`);
    });
    lines.push("————————————————");
    lines.push(`*TOTALE: ${fmtPrice(order.total_price)}*`);
    if (order.notes) { lines.push(""); lines.push(`_Note: ${order.notes}_`); }
    lines.push("");
    lines.push("_In attesa di conferma dal ristorante._");
    return lines.join("\n");
  };

  const submit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    // Build order preview + WhatsApp URL synchronously
    const tempCode = Math.random().toString(36).slice(2, 10).toUpperCase();
    const total = totals.price;
    const previewOrder = {
      id: tempCode,
      customer_name: customer.name.trim(),
      customer_phone: customer.phone.trim(),
      order_type: customer.type,
      table_number: customer.type === "dinein" ? customer.tableNumber.trim() : "",
      notes: customer.notes.trim(),
      total_price: total,
      items: items.map((it) => ({ name: it.meta.name, option: it.option, price: it.meta.price, qty: it.qty })),
    };
    const text = buildWhatsappText(previewOrder);
    const waUrl = `https://wa.me/${CONTACTS.cellIntl}?text=${encodeURIComponent(text)}`;

    // Fire-and-forget save to DB in background
    setSubmitting(true);
    const payload = {
      customer_name: previewOrder.customer_name,
      customer_phone: previewOrder.customer_phone,
      order_type: previewOrder.order_type,
      address: "",
      table_number: previewOrder.table_number,
      notes: previewOrder.notes,
      items: items.map((it) => ({ menu_id: it.id, name: it.meta.name, option: it.option || null, price: it.meta.price, qty: it.qty })),
    };
    axios.post(`${API}/orders`, payload).catch(() => {}).finally(() => setSubmitting(false));

    // Redirect to WhatsApp IMMEDIATELY (browser-friendly, user-initiated)
    window.location.href = waUrl;
  };

  const startNew = () => {
    clear();
    setCustomer(initialCustomer);
    setConfirmed(null);
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-ink border-l border-white/10 text-white p-0 overflow-y-auto" data-testid="cart-drawer">
        <SheetHeader className="p-6 pb-4 border-b border-white/5">
          <SheetTitle className="text-white font-display text-2xl flex items-center gap-2">
            <ShoppingBag size={20} className="text-gold" />
            {confirmed ? t("cart_sent_title") : t("cart_title")}
          </SheetTitle>
        </SheetHeader>

        {confirmed ? (
          <div className="p-6 space-y-6" data-testid="cart-success">
            <div className="w-14 h-14 rounded-full grid place-items-center bg-gold/15 border border-gold/40">
              <Check className="text-gold" size={26} />
            </div>
            <div>
              <h3 className="font-display text-2xl font-semibold">Ordine ricevuto!</h3>
              <p className="mt-2 text-white/65 text-sm leading-relaxed">
                In attesa di <span className="text-gold">conferma dal ristorante</span> via WhatsApp. Ti contatteranno al più presto.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-xl bg-ash/60 border border-white/5">
                <div className="text-white/50 text-[10px] uppercase tracking-widest">Codice</div>
                <div className="mt-1 font-mono">{confirmed.order.id.slice(0, 8).toUpperCase()}</div>
              </div>
              <div className="p-3 rounded-xl bg-ash/60 border border-white/5">
                <div className="text-white/50 text-[10px] uppercase tracking-widest">Totale</div>
                <div className="mt-1 text-gold font-display">{fmtPrice(confirmed.order.total_price)}</div>
              </div>
            </div>
            <a href={confirmed.waUrl} target="_blank" rel="noopener noreferrer" data-testid="cart-open-whatsapp"
              className="w-full inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1FBA57] text-white px-6 py-3.5 rounded-full font-medium transition-colors">
              <MessageCircle size={17} /> Apri WhatsApp
            </a>
            <button onClick={startNew} data-testid="cart-new-order-btn"
              className="w-full text-sm text-white/65 hover:text-white border border-white/10 hover:border-white/30 px-6 py-3 rounded-full transition-colors">
              Nuovo ordine
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 space-y-6" data-testid="cart-empty">
            <div className="text-center pt-4">
              <div className="w-14 h-14 mx-auto rounded-full grid place-items-center bg-ash border border-white/10 mb-4">
                <ShoppingBag className="text-white/40" size={20} />
              </div>
              <p className="text-white/60">Il carrello è vuoto.</p>
              <button onClick={() => setOpen(false)} className="mt-4 text-sm text-gold hover:text-white transition-colors" data-testid="cart-continue-shopping">
                ← Torna al menu
              </button>
            </div>
            <DeliveryPartners compact />
          </div>
        ) : (
          <>
            <div className="p-4 space-y-2" data-testid="cart-items">
              {items.map((it) => (
                <div key={`${it.id}-${it.option || ""}`} className="flex items-start gap-3 p-3 rounded-xl bg-ash/60 border border-white/5" data-testid={`cart-line-${it.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white line-clamp-1">{it.meta.name}</div>
                    {it.option && <div className="text-[11px] text-gold/80 mt-0.5">{it.option}</div>}
                    <div className="text-xs text-white/50 mt-1">{fmtPrice(it.meta.price)} {it.meta.unit && `· ${it.meta.unit}`}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => dec(it.id, it.option)} data-testid={`cart-dec-${it.id}`} className="w-7 h-7 rounded-full grid place-items-center bg-black/40 border border-white/10 hover:border-white/30 transition-colors">
                      <Minus size={12} />
                    </button>
                    <span className="w-5 text-center text-sm text-white">{it.qty}</span>
                    <button onClick={() => add(it.id, it.option)} data-testid={`cart-inc-${it.id}`} className="w-7 h-7 rounded-full grid place-items-center bg-black/40 border border-white/10 hover:border-white/30 transition-colors">
                      <Plus size={12} />
                    </button>
                  </div>
                  <div className="text-right shrink-0 min-w-[60px]">
                    <div className="text-sm text-gold font-medium">{fmtPrice(it.subtotal)}</div>
                    <button onClick={() => remove(it.id, it.option)} data-testid={`cart-remove-${it.id}`} className="text-[10px] text-white/40 hover:text-red-400 mt-1 inline-flex items-center gap-1 transition-colors">
                      <Trash2 size={10} /> Rimuovi
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-4">
              <div className="rounded-xl bg-ember/10 border border-ember/25 p-4 flex items-center justify-between" data-testid="cart-total">
                <span className="text-sm text-white/70">Totale ({totals.count} {totals.count === 1 ? "piatto" : "piatti"})</span>
                <span className="font-display text-2xl text-gold">{fmtPrice(totals.price)}</span>
              </div>
            </div>

            <form onSubmit={submit} className="p-4 space-y-4" data-testid="cart-checkout-form">
              {/* Order type toggle */}
              <div>
                <span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Modalità</span>
                <div className="grid grid-cols-2 gap-2" data-testid="cart-type-toggle">
                  <button
                    type="button"
                    onClick={() => setCustomer((c) => ({ ...c, type: "takeaway" }))}
                    data-testid="cart-type-takeaway"
                    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm border transition-colors ${
                      customer.type === "takeaway" ? "bg-ember border-ember text-white" : "bg-black/40 border-white/10 text-white/70 hover:border-white/30"
                    }`}
                  >
                    <Store size={14} /> {t("cart_takeaway")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomer((c) => ({ ...c, type: "dinein" }))}
                    data-testid="cart-type-dinein"
                    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm border transition-colors ${
                      customer.type === "dinein" ? "bg-ember border-ember text-white" : "bg-black/40 border-white/10 text-white/70 hover:border-white/30"
                    }`}
                  >
                    <Users size={14} /> {t("cart_dinein")}
                  </button>
                </div>
              </div>

              {customer.type === "dinein" && (
                <label className="block" data-testid="cart-field-table">
                  <span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Numero tavolo (opzionale)</span>
                  <input
                    value={customer.tableNumber}
                    onChange={setField("tableNumber")}
                    data-testid="cart-input-table"
                    placeholder="es. 5 — o lascia vuoto se non lo conosci"
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold transition-colors"
                  />
                </label>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Input label="Nome" required value={customer.name} onChange={setField("name")} placeholder="Marco Rossi" testId="cart-input-name" />
                <Input label="Telefono" required value={customer.phone} onChange={setField("phone")} placeholder="+39 344..." testId="cart-input-phone" inputMode="tel" />
              </div>

              <label className="block">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Note (opzionale)</span>
                <textarea value={customer.notes} onChange={setField("notes")} data-testid="cart-input-notes" rows={2}
                  placeholder="Allergie, richieste particolari..."
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold resize-none transition-colors" />
              </label>

              <div className="rounded-xl bg-black/40 border border-gold/20 p-3.5 flex items-start gap-2.5" data-testid="cart-info-status">
                <Info size={15} className="text-gold mt-0.5 shrink-0" />
                <div className="text-xs text-white/70 leading-relaxed">
                  L'ordine è <span className="text-gold">in attesa di conferma dal ristorante</span> via WhatsApp. Per consegna a domicilio usa Just Eat o Glovo qui sotto.
                </div>
              </div>

              <button type="submit" disabled={submitting || items.length === 0} data-testid="cart-send-whatsapp-btn"
                className="w-full inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1FBA57] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3.5 rounded-full font-medium transition-colors shadow-[0_10px_30px_-10px_rgba(37,211,102,0.6)]">
                {submitting ? <><Loader2 size={17} className="animate-spin" /> {t("cart_sending")}</> : <><MessageCircle size={17} /> {t("cart_send")}</>}
              </button>

              <button type="button" onClick={clear} data-testid="cart-clear-btn" className="w-full text-xs text-white/50 hover:text-red-400 transition-colors">
                {t("cart_clear")}
              </button>
            </form>

            <div className="px-4 pb-6">
              <DeliveryPartners />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DeliveryPartners({ compact = false }) {
  return (
    <div className="rounded-2xl bg-ash/60 border border-white/10 p-4" data-testid="cart-delivery-partners">
      <div className="flex items-center gap-2 mb-3">
        <Truck size={14} className="text-gold" />
        <span className="text-xs uppercase tracking-[0.2em] text-gold font-medium">Consegna a domicilio</span>
      </div>
      {!compact && <p className="text-xs text-white/60 mb-4 leading-relaxed">Ordina la consegna con i nostri partner ufficiali:</p>}
      <div className="grid grid-cols-2 gap-2">
        <a href={CONTACTS.delivery.justeat} target="_blank" rel="noopener noreferrer" data-testid="cart-justeat-link"
          className="inline-flex items-center justify-center gap-1.5 bg-[#FF8000] hover:bg-[#e07300] text-white text-xs font-medium px-3 py-2.5 rounded-xl transition-colors">
          Just Eat <ExternalLink size={11} />
        </a>
        <a href={CONTACTS.delivery.glovo} target="_blank" rel="noopener noreferrer" data-testid="cart-glovo-link"
          className="inline-flex items-center justify-center gap-1.5 bg-[#00A082] hover:bg-[#008468] text-white text-xs font-medium px-3 py-2.5 rounded-xl transition-colors">
          Glovo <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
}

function Input({ label, testId, ...rest }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">{label}</span>
      <input {...rest} data-testid={testId}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold transition-colors" />
    </label>
  );
}
